import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
// Bootstrap CSS
import "bootstrap/dist/css/bootstrap.min.css";
// Bootstrap Bundle JS
import "bootstrap/dist/js/bootstrap.bundle.min";
import { BrowserRouter } from 'react-router-dom';
import {HeroUIProvider} from "@heroui/react";
import { Provider } from "react-redux";
import { store } from './redux/store';
import {ToastProvider} from "@heroui/toast";
import axios from "axios";
import { MsalProvider } from "@azure/msal-react";
import { msalConfig } from './authConfig';
import { PublicClientApplication } from '@azure/msal-browser';

const msalInstance = new PublicClientApplication(msalConfig);
const root = ReactDOM.createRoot(document.getElementById('root'));

axios.defaults.baseURL = process.env.REACT_APP_BACKEND_URL; // or your deployed Flask API

// Every backend call carries credentials; wsgi.py checks them on each
// request. One injection point for both HTTP clients — axios through an
// interceptor, the raw fetch() call sites through a wrapper — and only for
// requests aimed at the backend (Graph and blob-storage URLs pass untouched).
//
// The credential is a bearer token Entra issues to the signed-in user for
// this API's own scope; the backend verifies it on every request. A missing
// or rejected token is reported once in the console with the AADSTS code,
// which is the useful part. (The shared X-Api-Key the frontend used to send
// is gone: a key inlined into a public bundle is not a secret. The backend
// keeps a key only for its own worker self-calls.)
const API_SCOPE = process.env.REACT_APP_API_SCOPE; // api://<api app id>/access_as_user
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// acquireTokenSilent needs an initialised instance. App.js initialises it
// again inside an effect; msal-browser treats the second call as a no-op.
const msalReady = msalInstance
  .initialize()
  .catch((e) => console.error("[auth] MSAL initialise failed:", e));

let tokenInFlight = null;
let tokenAnnounced = false;
const lastWarned = {};

function warnOnce(code, message) {
  // A failing token call would otherwise warn on every request.
  const now = Date.now();
  if (lastWarned[code] && now - lastWarned[code] < 60_000) return;
  lastWarned[code] = now;
  console.warn(`[auth] no API token (${code}): ${message}`);
}

async function getApiToken() {
  if (!API_SCOPE) return null;
  if (tokenInFlight) return tokenInFlight;
  tokenInFlight = (async () => {
    try {
      await msalReady;
      const account =
        msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
      if (!account) {
        // Basic login, or not signed in yet: nothing to request a token as.
        warnOnce("no_msal_account", "signed in without Microsoft, so no API token can be requested");
        return null;
      }
      const result = await msalInstance.acquireTokenSilent({
        scopes: [API_SCOPE],
        account,
      });
      if (!tokenAnnounced && result?.accessToken) {
        tokenAnnounced = true;
        console.info(`[auth] API token acquired for ${API_SCOPE}`);
      }
      return result?.accessToken || null;
    } catch (e) {
      warnOnce(e?.errorCode || e?.name || "error", e?.errorMessage || e?.message || String(e));
      // "Not consented" (AADSTS65001 arrives as invalid_grant) cannot be fixed
      // silently: once per browser session, ask interactively so the user
      // sees Entra's consent screen (or its "needs admin approval" message).
      if (needsInteraction(e)) askForConsentOnce();
      return null;
    } finally {
      tokenInFlight = null;
    }
  })();
  return tokenInFlight;
}

const CONSENT_ASKED_KEY = "pdg.consent.asked";
const needsInteraction = (e) =>
  ["interaction_required", "consent_required", "login_required", "invalid_grant"].includes(
    e?.errorCode
  );

function askForConsentOnce() {
  try {
    if (sessionStorage.getItem(CONSENT_ASKED_KEY)) return;
    sessionStorage.setItem(CONSENT_ASKED_KEY, "1");
  } catch (_) {
    return; // no sessionStorage: do not risk a redirect loop
  }
  console.info("[auth] asking for consent interactively (once per session)");
  msalInstance
    .acquireTokenRedirect({ scopes: [API_SCOPE] })
    .catch((err) => console.error("[auth] consent redirect failed:", err));
}

async function withBackendCredentials(headers) {
  if (!headers.has("Authorization")) {
    const token = await getApiToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }
  return headers;
}

// A 401 that names the token (not the key) means enforcement is on and the
// backend did not accept it: ask Entra for a fresh one once, and if that
// cannot happen silently, send the user to sign in again. One redirect at a
// time, so a page full of polling requests cannot bounce the user repeatedly.
let redirecting = false;
const isTokenRejection = (text) => /Invalid token|Missing credentials/.test(text || "");

async function refreshApiToken() {
  if (!API_SCOPE) return null;
  try {
    await msalReady;
    const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
    if (!account) return null;
    const result = await msalInstance.acquireTokenSilent({
      scopes: [API_SCOPE],
      account,
      forceRefresh: true,
    });
    return result?.accessToken || null;
  } catch (e) {
    if (!redirecting) {
      redirecting = true;
      console.warn("[auth] token refresh failed, signing in again:", e?.errorCode || e?.message);
      msalInstance.acquireTokenRedirect({ scopes: [API_SCOPE] }).catch((err) => {
        redirecting = false;
        console.error("[auth] sign-in redirect failed:", err);
      });
    }
    return null;
  }
}

const isBackendUrl = (url) => !!BACKEND_URL && url.startsWith(BACKEND_URL);

const nativeFetch = window.fetch.bind(window);
window.fetch = async (input, init = {}) => {
  const url = typeof input === "string" ? input : (input?.url ?? String(input));
  if (!isBackendUrl(url)) return nativeFetch(input, init);
  const headers = await withBackendCredentials(
    new Headers(init.headers || (input instanceof Request ? input.headers : undefined))
  );
  const response = await nativeFetch(input, { ...init, headers });
  if (response.status !== 401 || !headers.has("Authorization")) return response;
  if (!isTokenRejection(await response.clone().text())) return response; // a key problem, not ours
  const fresh = await refreshApiToken();
  if (!fresh) return response;
  headers.set("Authorization", `Bearer ${fresh}`);
  return nativeFetch(input, { ...init, headers });
};

axios.interceptors.request.use(async (config) => {
  const url = config.url || "";
  const full = /^https?:\/\//.test(url) ? url : (config.baseURL || "") + url;
  if (!isBackendUrl(full)) return config;
  config.headers = config.headers || {};
  if (!config.headers["Authorization"]) {
    const token = await getApiToken();
    if (token) config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(undefined, async (error) => {
  const { response, config } = error || {};
  const body = typeof response?.data === "string" ? response.data : JSON.stringify(response?.data || "");
  const tokenWasSent = !!config?.headers?.Authorization;
  if (response?.status !== 401 || !config || config.__retried || !tokenWasSent || !isTokenRejection(body)) {
    throw error;
  }
  const fresh = await refreshApiToken();
  if (!fresh) throw error;
  config.__retried = true;
  config.headers.Authorization = `Bearer ${fresh}`;
  return axios(config);
});



root.render(
  //<React.StrictMode>
  <Provider store={store}>
    <BrowserRouter>
      <HeroUIProvider>
        <ToastProvider />
          <MsalProvider instance={msalInstance}>
            <App  classname="bg-surfaceContainer" />
          </MsalProvider>
      </HeroUIProvider>
    </BrowserRouter>
  </Provider>

  //</React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
